/**
 * PreviewContextBar.tsx
 * Sticky context bar shown at the top of the preview step: what will print
 * (Excel source, template, paper size, page count) with inline edit affordances.
 * Extracted from DocxMarkerWizard to keep that file small.
 */
import { FileUp, Pencil, Printer, ListChecks, PanelRightClose, PanelRightOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatPaper, type CurrentPaper } from "@/utils/paperFormat";
import { useI18n } from "@/i18n";

export interface PreviewContextBarProps {
  onBack: () => void;
  excelFileName: string;
  selectedSheet: string;
  templateName: string | null;
  onOpenTemplatePicker: () => void;
  printPaper: CurrentPaper | null;
  onOpenProfileEditor: () => void;
  enabledPageCount: number;
  previewPanelOpen: boolean;
  onTogglePanel: () => void;
}

export function PreviewContextBar(props: PreviewContextBarProps) {
  const { t } = useI18n();
  const {
    onBack,
    excelFileName,
    selectedSheet,
    templateName,
    onOpenTemplatePicker,
    printPaper,
    onOpenProfileEditor,
    enabledPageCount,
    previewPanelOpen,
    onTogglePanel,
  } = props;

  return (
    <div className="lg:col-span-12 sticky top-[3.6rem] z-30 -mx-0.5 rounded-lg border border-border/80 bg-background/95 px-2.5 py-2 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="h-8 gap-1.5 px-2 text-xs"
        >
          {t("preview.backToData")}
        </Button>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-1.5">
          {/* Excel source */}
          <div className="flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/40 px-2 py-1">
            <FileUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{t("preview.dataLabel")}</p>
              <p className="max-w-[140px] truncate text-[11px] font-medium text-foreground sm:max-w-[180px]" title={excelFileName || selectedSheet || ""}>
                {excelFileName || selectedSheet || t("preview.noExcel")}
              </p>
            </div>
          </div>
          {/* Template (editable) */}
          <button
            type="button"
            onClick={onOpenTemplatePicker}
            className="group flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/40 px-2 py-1 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
            title={t("preview.templateLabel")}
            aria-label={t("preview.templateLabel")}
            data-tour="tour-template-chip"
          >
            <Pencil className="h-3.5 w-3.5 shrink-0 text-primary/70" />
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{t("preview.templateLabel")}</p>
              <p className="max-w-[140px] truncate text-[11px] font-medium text-foreground sm:max-w-[180px]">
                {templateName ?? t("preview.noTemplate")}
              </p>
            </div>
          </button>
          {/* Paper/printer (editable) */}
          <button
            type="button"
            onClick={onOpenProfileEditor}
            className="group flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/40 px-2 py-1 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
            title={t("preview.paperLabel")}
            aria-label={t("preview.paperLabel")}
            data-tour="tour-paper-chip"
          >
            <Printer className="h-3.5 w-3.5 shrink-0 text-primary/70" />
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{t("preview.paperLabel")}</p>
              <p className="text-[11px] font-medium text-foreground">{formatPaper(printPaper)}</p>
            </div>
          </button>
          {/* Page count */}
          <div
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-2 py-1",
              enabledPageCount > 0
                ? "border-primary/30 bg-primary/10"
                : "border-border/70 bg-muted/40"
            )}
          >
            <ListChecks className="h-3.5 w-3.5 shrink-0 text-primary" />
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{t("preview.willPrintLabel")}</p>
              <p className="text-[11px] font-semibold text-foreground">
                {t("preview.pages", { count: enabledPageCount })}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onTogglePanel}
            className="h-8 w-8"
            title={previewPanelOpen ? t("preview.collapsePanel") : t("preview.expandPanel")}
          >
            {previewPanelOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
